import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import { onIdTokenChanged } from 'firebase/auth'
import { firebaseAuth } from './../../api/FirebaseAuth'
import env from './../../environments/environment'
import StorageService from './Storage.service'

// ---------------------------------------------------------------------------
// FIREBASE API STYLE - READ BEFORE MERGING WITH master
//
// master (firebase@8) uses the v8 *namespaced* API here:
//     import firebase from 'firebase/app'
//     firebase.auth().currentUser
//
// This branch is on firebase@10, where 'firebase/app' has NO default export and
// there is no namespaced auth accessor, so master's version cannot run here.
//
// It must also NOT be "fixed" by switching to the compat entrypoints. Compat keeps
// its OWN app registry (@firebase/app-compat), separate from the modular one
// (@firebase/app). src/api/FirebaseAuth.js initializes the app through the MODULAR
// API, so the compat registry is never populated and the compat auth accessor
// throws `app-compat/no-app` - which silently disabled Echo (UAD-3606/React18 Upgrade:
// the live leaderboard fell back to REST-only polling with no user-visible error).
//
// Initializing a second, compat app would not help either: it would carry its own
// auth instance with currentUser === null, since users sign in through the modular
// `firebaseAuth` below. Echo would then send an empty token instead of throwing.
//
// So: always resolve auth from the single exported `firebaseAuth` instance.
// When resolving a merge conflict against master, KEEP THIS SIDE.
// ---------------------------------------------------------------------------

let echoInstance = null
let idTokenUnsubscribe = null

const getCurrentToken = async () => {
  const user = firebaseAuth.currentUser
  if (user) {
    try {
      return await user.getIdToken(false)
    } catch (e) {
      console.warn('Echo.service: firebase.getIdToken failed, falling back to StorageService', e)
    }
  }
  return StorageService.getToken()
}

const getApiHost = () => env?.api?.automation_url || ''

const init = async () => {
  if (echoInstance) return echoInstance

  // Never open a private-channel socket unauthenticated — Reverb's /broadcasting/auth
  // would just 401. Callers gate on auth; this is belt-and-suspenders so a stray
  // unauthenticated call (e.g. on the public login page) can't leak an Echo connection.
  if (!firebaseAuth.currentUser) return null

  const key = process.env.REACT_APP_REVERB_APP_KEY
  const host = process.env.REACT_APP_REVERB_HOST
  const port = parseInt(process.env.REACT_APP_REVERB_PORT || '443', 10)
  const scheme = process.env.REACT_APP_REVERB_SCHEME || 'https'

  console.log('[Echo.service] Reverb config (compile-time env):', {
    key: key ? `${String(key).slice(0, 8)}…` : '(missing)',
    host: host || '(missing)',
    port,
    scheme,
    apiHost: getApiHost() || '(missing)',
  })

  if (!key || !host) {
    const missing = [!key && 'REACT_APP_REVERB_APP_KEY', !host && 'REACT_APP_REVERB_HOST'].filter(Boolean).join(', ')
    throw new Error(
      `[Echo.service] Cannot initialize — missing env var(s): ${missing}. ` +
        `CRA bakes process.env.REACT_APP_* at boot — set them in .env at the repo root and fully restart \`npm start\`.`
    )
  }

  if (typeof window !== 'undefined') {
    window.Pusher = Pusher
  }

  const token = await getCurrentToken()
  const isHttps = scheme !== 'http'

  echoInstance = new Echo({
    broadcaster: 'reverb',
    key,
    wsHost: host,
    wsPort: port,
    wssPort: port,
    forceTLS: isHttps,
    enabledTransports: ['ws', 'wss'],
    authEndpoint: `${getApiHost()}/broadcasting/auth`,
    auth: {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        Accept: 'application/json',
      },
    },
  })

  if (idTokenUnsubscribe && typeof idTokenUnsubscribe === 'function') {
    idTokenUnsubscribe()
  }
  idTokenUnsubscribe = onIdTokenChanged(firebaseAuth, async user => {
    if (!user || !echoInstance) return
    try {
      const fresh = await user.getIdToken(false)
      echoInstance.connector.options.auth.headers.Authorization = `Bearer ${fresh}`
    } catch (e) {
      console.warn('Echo.service: token refresh failed', e)
    }
  })

  if (typeof window !== 'undefined') {
    window.Echo = echoInstance
  }

  return echoInstance
}

const get = () => echoInstance

const disconnect = () => {
  if (idTokenUnsubscribe && typeof idTokenUnsubscribe === 'function') {
    idTokenUnsubscribe()
    idTokenUnsubscribe = null
  }
  if (echoInstance) {
    echoInstance.disconnect()
    echoInstance = null
    if (typeof window !== 'undefined') {
      delete window.Echo
    }
  }
}

const EchoService = {
  init,
  get,
  disconnect,
}

export default EchoService
